import { useEffect, useRef, useState, type FormEvent } from "react";
import { CircleCheck, MailCheck } from "lucide-react";
import { z } from "zod";
import {
  btnPrimary,
  btnSecondary,
  fieldErrorClass,
  inputClass,
} from "./admin-ui";
import { track } from "./lib/analytics";
import { apiFetch, saveAuthToken } from "./lib/auth";
import { PasswordInput } from "./password-input";

export type GuestAccount = {
  email: string;
  state: "activate" | "existing" | "active";
  locked?: boolean;
};

const copy = {
  paidKicker: "Pago confirmado",
  codeTitle: "Activa tu cuenta",
  codeBody: (email: string) =>
    `Te enviamos un código de 6 dígitos a ${email}. Escríbelo aquí para confirmar que ese correo es tuyo.`,
  codeLabel: "Código de 6 dígitos",
  codeSubmit: "Confirmar código",
  codeChecking: "Revisando…",
  resend: "Reenviar el código",
  resending: "Enviando…",
  resent: (email: string) => `Listo, te enviamos un código nuevo a ${email}.`,
  wrongEmail: "¿Escribiste mal tu correo?",
  fixEmail: "Corrígelo aquí",
  newEmailLabel: "Tu correo correcto",
  newEmailSubmit: "Enviar el código a este correo",
  cancel: "Cancelar",
  passwordTitle: "Crea tu acceso",
  passwordBody: (email: string) =>
    `Tu correo ${email} quedó confirmado. Ahora elige cómo te llamamos y tu contraseña.`,
  nameLabel: "Tu nombre",
  passwordLabel: "Crea una contraseña",
  passwordHelp: "Mínimo 10 caracteres, con al menos una letra y un número.",
  confirmLabel: "Confírmala",
  activate: "Entrar a mi curso",
  activating: "Activando tu cuenta…",
  existingTitle: "Tu curso ya está en tu cuenta",
  existingBody: (email: string) =>
    `Ya tenías una cuenta con ${email}: le sumamos el curso. Inicia sesión y lo encuentras ahí.`,
  activeTitle: "Tu cuenta ya está activa",
  activeBody:
    "Inicia sesión con tu correo y tu contraseña para entrar al curso.",
  login: "Iniciar sesión",
  errors: {
    invalid: "Ese código no es correcto. Revísalo e inténtalo de nuevo.",
    expired: "Ese código venció. Pide uno nuevo.",
    locked: "Hubo demasiados intentos. Pide un código nuevo.",
    claimed: "Esta cuenta ya está activa. Inicia sesión.",
    too_soon: "Espera unos segundos antes de pedir otro código.",
    too_many_codes:
      "Ya enviamos varios códigos. Escríbenos a hey404lab@gmail.com y te ayudamos.",
    too_many_changes:
      "Ya cambiaste el correo varias veces. Escríbenos a hey404lab@gmail.com.",
    same_email: "Ese es el mismo correo al que enviamos el código.",
    account_in_use:
      "No pudimos cambiar el correo. Escríbenos a hey404lab@gmail.com.",
    invalid_email: "Escribe un correo válido, como nombre@gmail.com.",
    name: "Escribe tu nombre (mínimo 2 letras).",
    password: "Mínimo 10 caracteres, con al menos una letra y un número.",
    mismatch: "Las contraseñas no coinciden.",
    generic: "Algo salió mal. Inténtalo de nuevo.",
  } as Record<string, string>,
};

const errorOf = (error: unknown) =>
  ((error as { body?: { error?: string } }).body?.error ?? "generic") as string;

const passwordSchema = z
  .string()
  .min(10)
  .regex(/[A-Za-z]/)
  .regex(/\d/);

/**
 * What a guest buyer sees once the payment is confirmed: activate the new
 * account (code → name + password → signed in), or sign in to the account the
 * email already had.
 */
export function GuestActivation({
  orderId,
  account,
  courseSlug,
}: {
  orderId: string;
  account: GuestAccount;
  courseSlug: string | null;
}) {
  const coursePath = courseSlug ? `/courses/${courseSlug}` : "/courses";
  const loginHref = `/login?next=${encodeURIComponent(coursePath)}`;
  const [state, setState] = useState(account.state);
  const [email, setEmail] = useState(account.email);
  // Kept in the tab's session storage too, so a reload (or the browser
  // discarding a background tab) doesn't lose the code or the step.
  const storageKey = `hey404:activation:${orderId}`;
  const saved = (() => {
    try {
      return JSON.parse(sessionStorage.getItem(storageKey) ?? "null") as {
        code?: string;
        step?: "code" | "password";
      } | null;
    } catch {
      return null;
    }
  })();
  const [step, setStep] = useState<"code" | "password">(saved?.step ?? "code");
  const [code, setCode] = useState(
    () =>
      new URLSearchParams(window.location.search).get("code") ??
      saved?.code ??
      "",
  );
  useEffect(() => {
    try {
      sessionStorage.setItem(storageKey, JSON.stringify({ code, step }));
    } catch {
      // Storage unavailable: the code just isn't remembered across reloads.
    }
  }, [storageKey, code, step]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(account.locked ? copy.errors.locked! : "");
  const [notice, setNotice] = useState("");
  const [fixing, setFixing] = useState(false);
  const [newEmail, setNewEmail] = useState("");
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const autoChecked = useRef(false);

  async function verify(value = code) {
    if (busy || !/^\d{6}$/.test(value.trim())) {
      setError(copy.errors.invalid!);
      return;
    }
    setBusy(true);
    setError("");
    setNotice("");
    try {
      await apiFetch(`/api/checkout/orders/${orderId}/activation/verify`, {
        method: "POST",
        body: JSON.stringify({ code: value.trim() }),
      });
      setStep("password");
      track("account_code_verified");
    } catch (err) {
      const reason = errorOf(err);
      if (reason === "claimed") setState("active");
      setError(copy.errors[reason] ?? copy.errors.generic!);
    } finally {
      setBusy(false);
    }
  }

  // Opened from the email button: the code comes in the link, check it now.
  useEffect(() => {
    if (autoChecked.current || !code || state !== "activate" || step !== "code")
      return;
    autoChecked.current = true;
    void verify(code);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function resend() {
    if (busy) return;
    setBusy(true);
    setError("");
    setNotice("");
    try {
      const result = await apiFetch<{ email: string }>(
        `/api/checkout/orders/${orderId}/activation/resend`,
        { method: "POST", body: "{}" },
      );
      setCode("");
      setNotice(copy.resent(result.email));
    } catch (err) {
      setError(copy.errors[errorOf(err)] ?? copy.errors.generic!);
    } finally {
      setBusy(false);
    }
  }

  async function changeEmail(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (busy) return;
    if (!z.string().email().safeParse(newEmail.trim()).success) {
      setError(copy.errors.invalid_email!);
      return;
    }
    setBusy(true);
    setError("");
    setNotice("");
    try {
      const result = await apiFetch<{
        state: "activate" | "existing";
        email: string;
      }>(`/api/checkout/orders/${orderId}/activation/email`, {
        method: "POST",
        body: JSON.stringify({ email: newEmail.trim() }),
      });
      setEmail(result.email);
      setState(result.state);
      setFixing(false);
      setNewEmail("");
      setCode("");
      if (result.state === "activate") setNotice(copy.resent(result.email));
    } catch (err) {
      setError(copy.errors[errorOf(err)] ?? copy.errors.generic!);
    } finally {
      setBusy(false);
    }
  }

  async function activate(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (busy) return;
    const values = Object.fromEntries(new FormData(event.currentTarget)) as {
      name: string;
      password: string;
      confirm: string;
    };
    const nextErrors: Record<string, string> = {};
    if (values.name.trim().length < 2) nextErrors.name = copy.errors.name!;
    if (!passwordSchema.safeParse(values.password).success)
      nextErrors.password = copy.errors.password!;
    else if (values.password !== values.confirm)
      nextErrors.confirm = copy.errors.mismatch!;
    setFieldErrors(nextErrors);
    if (Object.keys(nextErrors).length) return;
    setBusy(true);
    setError("");
    try {
      const { token } = await apiFetch<{ token: string }>(
        `/api/checkout/orders/${orderId}/activation/complete`,
        {
          method: "POST",
          body: JSON.stringify({
            code: code.trim(),
            name: values.name.trim(),
            password: values.password,
          }),
        },
      );
      saveAuthToken(token);
      track("account_activated");
      // A full load so the app boots with the new session, straight into the
      // course that was just bought.
      window.location.assign(coursePath);
    } catch (err) {
      const reason = errorOf(err);
      if (reason === "claimed") setState("active");
      else if (reason !== "invalid_body") setStep("code");
      setError(copy.errors[reason] ?? copy.errors.generic!);
      setBusy(false);
    }
  }

  const card = "border-ink bg-bone shadow-brutal-lg border-surface p-6 sm:p-8";
  const kicker = (
    <p className="flex items-center gap-2 font-mono text-xs font-bold uppercase tracking-[0.08em]">
      <CircleCheck size={16} aria-hidden="true" /> {copy.paidKicker}
    </p>
  );

  if (state !== "activate")
    return (
      <div className={card}>
        {kicker}
        <h1 className="font-display mt-3 text-3xl font-extrabold sm:text-4xl">
          {state === "existing" ? copy.existingTitle : copy.activeTitle}
        </h1>
        <p className="text-ink-soft mt-3">
          {state === "existing" ? copy.existingBody(email) : copy.activeBody}
        </p>
        <a href={loginHref} className={`${btnPrimary} mt-7 px-6 py-4 text-lg`}>
          {copy.login} →
        </a>
      </div>
    );

  return (
    <div className={card}>
      {kicker}
      {step === "code" ? (
        <>
          <h1 className="font-display mt-3 text-3xl font-extrabold sm:text-4xl">
            {copy.codeTitle}
          </h1>
          <p className="text-ink-soft mt-3 flex gap-2">
            <MailCheck
              size={20}
              className="mt-0.5 shrink-0"
              aria-hidden="true"
            />
            <span>{copy.codeBody(email)}</span>
          </p>
          <form
            className="mt-6"
            onSubmit={(event) => {
              event.preventDefault();
              void verify();
            }}
          >
            <label
              htmlFor="activation-code"
              className="block text-sm font-bold"
            >
              {copy.codeLabel}
            </label>
            <div className="mt-1.5 flex flex-wrap gap-3">
              <input
                id="activation-code"
                value={code}
                onChange={(event) =>
                  setCode(event.target.value.replace(/\D/g, "").slice(0, 6))
                }
                inputMode="numeric"
                autoComplete="one-time-code"
                maxLength={6}
                placeholder="000000"
                aria-invalid={Boolean(error)}
                className={`${inputClass} mt-0 w-44 text-center font-mono text-2xl tracking-[0.3em]`}
              />
              <button
                type="submit"
                disabled={busy || code.length !== 6}
                className={`${btnPrimary} px-5 py-3`}
              >
                {busy ? copy.codeChecking : copy.codeSubmit}
              </button>
            </div>
          </form>
          {error && (
            <p className={`${fieldErrorClass} mt-3`} role="alert">
              {error}
            </p>
          )}
          {notice && (
            <p className="mt-3 text-sm font-bold" role="status">
              {notice}
            </p>
          )}
          <div className="border-ink/20 mt-6 space-y-2 border-t border-dashed pt-5 text-sm">
            <p>
              <button
                type="button"
                onClick={() => void resend()}
                disabled={busy}
                className="cursor-pointer font-bold underline"
              >
                {busy ? copy.resending : copy.resend}
              </button>
            </p>
            {fixing ? (
              <form onSubmit={changeEmail} className="pt-2">
                <label htmlFor="activation-email" className="block font-bold">
                  {copy.newEmailLabel}
                </label>
                <input
                  id="activation-email"
                  type="email"
                  inputMode="email"
                  autoComplete="email"
                  value={newEmail}
                  onChange={(event) => setNewEmail(event.target.value)}
                  placeholder="tu@correo.com"
                  className={inputClass}
                />
                <div className="mt-3 flex flex-wrap gap-3">
                  <button
                    type="submit"
                    disabled={busy}
                    className={`${btnPrimary} px-4 py-2.5 text-sm`}
                  >
                    {copy.newEmailSubmit}
                  </button>
                  <button
                    type="button"
                    onClick={() => setFixing(false)}
                    className={`${btnSecondary} px-4 py-2.5 text-sm`}
                  >
                    {copy.cancel}
                  </button>
                </div>
              </form>
            ) : (
              <p>
                {copy.wrongEmail}{" "}
                <button
                  type="button"
                  onClick={() => {
                    setFixing(true);
                    setError("");
                  }}
                  className="cursor-pointer font-bold underline"
                >
                  {copy.fixEmail}
                </button>
              </p>
            )}
          </div>
        </>
      ) : (
        <>
          <h1 className="font-display mt-3 text-3xl font-extrabold sm:text-4xl">
            {copy.passwordTitle}
          </h1>
          <p className="text-ink-soft mt-3">{copy.passwordBody(email)}</p>
          <form onSubmit={activate} className="mt-6 grid gap-4" noValidate>
            <label className="block">
              <span className="text-sm font-bold">{copy.nameLabel}</span>
              <input
                name="name"
                autoComplete="name"
                aria-invalid={Boolean(fieldErrors.name)}
                className={inputClass}
              />
              {fieldErrors.name && (
                <span className={fieldErrorClass}>{fieldErrors.name}</span>
              )}
            </label>
            <label className="block">
              <span className="text-sm font-bold">{copy.passwordLabel}</span>
              <PasswordInput
                name="password"
                autoComplete="new-password"
                aria-invalid={Boolean(fieldErrors.password)}
                className={inputClass}
              />
              <span className="text-ink-soft mt-1 block text-xs">
                {copy.passwordHelp}
              </span>
              {fieldErrors.password && (
                <span className={fieldErrorClass}>{fieldErrors.password}</span>
              )}
            </label>
            <label className="block">
              <span className="text-sm font-bold">{copy.confirmLabel}</span>
              <PasswordInput
                name="confirm"
                autoComplete="new-password"
                aria-invalid={Boolean(fieldErrors.confirm)}
                className={inputClass}
              />
              {fieldErrors.confirm && (
                <span className={fieldErrorClass}>{fieldErrors.confirm}</span>
              )}
            </label>
            {error && (
              <p className={fieldErrorClass} role="alert">
                {error}
              </p>
            )}
            <button
              type="submit"
              disabled={busy}
              className={`${btnPrimary} mt-2 px-6 py-4 text-lg`}
            >
              {busy ? copy.activating : `${copy.activate} →`}
            </button>
          </form>
        </>
      )}
    </div>
  );
}
